import { get, post, postTracking, put } from '@/services/http';
import { parseFrontendAppFields } from '@/utils/frontendAppFields';
import { UserPackageModel } from '@/models/user_package';
import { RuleList } from '@/collections/rules';
import { RuleModel } from '@/models/rule';
import { ResourceList } from '@/collections/resources';
import { EtlStatusList } from '@/collections/etl_statuses';
import { EventBus } from '@/main';
import { FEATURES, PREMIUM_MEASURE_CODES } from '@/config/featureConfig';
import {
  hasInventoryTransferPermission,
  needsInventoryScope,
  needsInventoryTransferScope,
  parseShopifyScopesKnown,
} from '@/utils/shopifyScopes';

interface ResourceCount {
  resource: string;
  modelCount: number;
  apiCount: number;
  startTime?: string;
  endTime?: string;
  limitStartTime?: string;
}

export class StoreModel {

  public get canShowOnboardCompleteConfirm() {
    if (this.config.confirmCorrectData !== 'true') {
      return true;
    }
    if (this.config.confirmRightReport !== 'true') {
      return true;
    }
    return false;
  }

  public get assistyPlan() {
    const mappingPlans: any = [
      {
        shopify: 'Plus',
        assisty: 'Shopify Plus',
      },
      {
        shopify: 'Advanced',
        assisty: 'Advanced Shopify',
      },
      {
        shopify: 'Basic',
        assisty: 'Basic Shopify',
      },
      {
        shopify: 'Shopify',
        assisty: 'Shopify',
      },
    ];

    const shopifyPlan = this.information.plan_display_name.toLowerCase();
    let assistyPlan = 'Shopify';
    for (const item of mappingPlans) {
      if (shopifyPlan.includes(item.shopify.toLowerCase())) {
        assistyPlan = item.shopify;
        break;
      }
    }
    return assistyPlan;
  }
  public id: number;
  public shopName: string;
  public slackConnected: boolean;
  public slackData: any;
  public etlStatus: string;
  public lastEtlTime: string;
  public passedInstallingFlow: string;
  public channelConnected: boolean;
  public channelData: any;
  public askedFirstQuestion: boolean;
  public merchantAppVersion: string;
  public systemAppVersion: string;
  public enableAthena: boolean;
  public isAthenaReady: boolean;
  public timeRangeLimit: string[];
  public information: any;
  public isAthenaFinished: boolean;
  public scheduleEtl: string;
  public isLockedReports: string;
  public installedAt: string;
  public hasSchemas: boolean;
  public config: any;
  public firstOrderAt: string;
  public firstOrderUpdatedAt: string;
  public offerAt: number;
  public currentPackage: UserPackageModel;
  public storeType?: string;
  public limitHistoryDate?: string;
  public rules: RuleList;
  public fullRules: RuleList;
  public resources: ResourceList = new ResourceList();
  public etlStatuses: EtlStatusList = new EtlStatusList();
  public currentStep: number = 0;
  public shopifyScopes: string = '';
  /**
   * Which app serves this store under the cohort routing (AS-11272 M2):
   * 'warbler' (this app) or 'atlas'. Null when `/stores/detail` has no usable
   * value; the shell guard then fails open.
   */
  public frontendApp: 'warbler' | 'atlas' | null = null;
  /**
   * AS-11272 M6: may the CALLING user move this store to atlas? Computed by
   * the bot (owner, C3 gate open, not on atlas yet); warbler never derives it.
   */
  public frontendAppOptInAvailable: boolean = false;
  public shopifyScopesKnown: boolean = true;
  public orderCount: ResourceCount = {
    resource: '',
    modelCount: 0,
    apiCount: 0,
  };
  public productCount: ResourceCount = {
    resource: '',
    modelCount: 0,
    apiCount: 0,
  };
  public customerCount: ResourceCount = {
    resource: '',
    modelCount: 0,
    apiCount: 0,
  };
  public locationCount: ResourceCount = {
    resource: '',
    modelCount: 0,
    apiCount: 0,
  };
  public inventoryItemCount: ResourceCount = {
    resource: '',
    modelCount: 0,
    apiCount: 0,
  };
  public customCollection: ResourceCount = {
    resource: '',
    modelCount: 0,
    apiCount: 0,
  };
  public smartCollection: ResourceCount = {
    resource: '',
    modelCount: 0,
    apiCount: 0,
  };
  // The GraphQL `collections` resource, which replaces the custom/smart pair
  // for merchants on the new extract. See @/utils/collectionsResourceCount.
  public collectionCount: ResourceCount = {
    resource: '',
    modelCount: 0,
    apiCount: 0,
  };

  // Individual ETL readiness flags (exposed for welcome page)
  public isProductsReady: boolean = false;
  public isSalesOrderOlapReady: boolean = false;
  public isSalesOrderOlapFinished: boolean = false;
  public isInventoryLevelsReady: boolean = false;

  constructor(data: any = {}) {
    this.id = data.id;
    this.shopName = data.shopName;
    this.slackConnected = data.slackConnected;
    this.slackData = data.slackData;
    this.etlStatus = data.etlStatus;
    this.lastEtlTime = data.lastEtlTime;
    this.passedInstallingFlow = data.passedInstallingFlow;
    this.channelConnected = data.channelConnected;
    this.channelData = data.channelData;
    this.askedFirstQuestion = data.askedFirstQuestion;
    this.merchantAppVersion = data.merchantAppVersion;
    this.systemAppVersion = data.systemAppVersion;
    this.enableAthena = data.enableAthena;
    this.isAthenaReady = data.isAthenaReady;
    this.isAthenaFinished = data.isAthenaFinished;
    this.timeRangeLimit = [];
    this.information = {};
    this.scheduleEtl = 'daily';
    this.isLockedReports = 'available';
    this.hasSchemas = false;
    this.installedAt = data.installedAt;
    this.config = data.config;
    this.firstOrderAt = data.firstOrderAt;
    this.firstOrderUpdatedAt = data.firstOrderUpdatedAt;
    this.offerAt = 0;
    this.currentPackage = new UserPackageModel();
    this.rules = new RuleList();
    this.fullRules = new RuleList();
    this.resources = new ResourceList();
    this.shopifyScopes = data.shopifyScopes;
    this.shopifyScopesKnown = parseShopifyScopesKnown(data.shopifyScopesKnown);
  }

  private _inflight: Map<string, Promise<any>> = new Map();

  public async fetch() {
    const key = 'fetch';
    if (this._inflight.has(key)) {
      return this._inflight.get(key);
    }
    const promise = (async () => {
      try {
        const preload = [];
        if (!this.currentPackage.id) {
          preload.push(this.currentPackage.getCurrent());
          preload.push(this.getRules());
          preload.push(this.resources.fetch());
        }
        await Promise.all(preload);

        if (!this.isAthenaFinished) {
          await this.getStoreData();
        }
      } finally {
        this._inflight.delete(key);
      }
    })();
    this._inflight.set(key, promise);
    return promise;
  }

  public async loadStoreType() {
    if (!this.storeType) {
      const storeTypeData: any = await get(`/stores/type`);
      this.storeType = storeTypeData.data.type;
    }
  }

  public async getStoreData() {
    const key = 'getStoreData';
    if (this._inflight.has(key)) {
      return this._inflight.get(key);
    }
    const promise = (async () => {
      try {
        const res: any = await get(`/stores/detail`);
        await this.mapData(res.data);
      } finally {
        this._inflight.delete(key);
      }
    })();
    this._inflight.set(key, promise);
    return promise;
  }

  // AS-11619: tracking is fire-and-forget — skipped without an identity, and a
  // 401 on it must never refresh, renew or evict the session (postTracking).
  public async sendEvent(event: string, data: any = {}) {
    await postTracking('/customerio/events', {
      name: event,
      data,
    });
  }

  public async startOffer(offerAt: number) {
    const res: any = await post('/stores/offer', {
      offerAt,
    });
  }

  public async confirmUnlockReports() {
    if (this.isLockedReports !== 'confirming') {
      return;
    }
    await put(`/stores/confirm_unlock_reports`);
    this.isLockedReports = 'available';
  }

  public async saveSettings() {
    await put(`/settings`, {
      contactName: this.config.contactName,
      contactEmail: this.config.contactEmail,
      phone: this.config.contactPhone,
      productCategory: this.config.productCategory,
      topReports: this.config.topReports,
      role: this.config.role,
      purpose: this.config.purpose,
      metrics: this.config.metrics,
      profile: this.config.profile,
      favoriteReports: this.config.favoriteReports,
      homepageDashboardId: this.config.homepageDashboardId,
      homepageDashboardType: this.config.homepageDashboardType,
      confirmRightReport: this.config.confirmRightReport + '',
      confirmCorrectData: this.config.confirmCorrectData + '',
      instantMessageChannel: this.config.instantMessageChannel,
      instantMessageAccount: this.config.instantMessageAccount,
      completeReplenishmentSetting: this.config.completeReplenishmentSetting + '',
      chatChannels: this.config.chatChannels,
    });
  }

  public async saveLanguage() {
    await put(`/settings/language`, {
      language: (this.config) ? this.config.language || 'en' : 'en',
    });
  }

  public async updateInstallationStatus() {
    await put(`/stores/${this.id}/installing_status`);
    this.passedInstallingFlow = 'passed';
  }

  public async getRules() {
    this.rules = new RuleList();
    const res: any = await get(`/store/rules`);
    if (res.data) {
      this.rules.mapData(res.data.rules);
      this.fullRules.mapData(res.data.fullRules);
    }
  }

  public async mapData(data: any = {}) {
    this.id = data.id;
    this.shopName = data.shop_name;
    this.slackConnected = data.slack_connected;
    this.slackData = data.slack_data;
    this.etlStatus = data.etl_status;
    this.passedInstallingFlow = data.passed_installing_flow;
    this.channelConnected = data.channel_connected;
    this.channelData = data.channel_data;
    this.askedFirstQuestion = data.asked_first_question;
    this.merchantAppVersion = data.merchant_app_version;
    this.systemAppVersion = data.system_app_version;
    this.enableAthena = data.enable_athena;
    this.information = data.information;
    this.scheduleEtl = data.schedule_etl;
    this.isLockedReports = data.is_locked_reports;
    this.installedAt = data.installed_at;
    this.hasSchemas = data.has_schemas === 'created';
    this.config = data.config;
    this.lastEtlTime = data.endTime;
    this.firstOrderAt = data.firstOrderAt;
    this.firstOrderUpdatedAt = data.firstOrderUpdatedAt;
    this.timeRangeLimit = [data.startTime, data.endTime];
    this.isAthenaReady = this.timeRangeLimit[0] !== this.timeRangeLimit[1];
    this.offerAt = Number(data.offerAt) || 0;
    this.limitHistoryDate = data.limitHistoryDate;
    this.etlStatuses = new EtlStatusList();
    this.etlStatuses.mapData(data.etlStatuses);
    this.currentStep = data.currentStep;
    
    this.shopifyScopes = data.shopifyScopes;
    const frontendAppFields = parseFrontendAppFields(data);
    this.frontendApp = frontendAppFields.frontendApp;
    this.frontendAppOptInAvailable = frontendAppFields.optInAvailable;
    this.shopifyScopesKnown = parseShopifyScopesKnown(data.shopifyScopesKnown);
    try {
      if (this.passedInstallingFlow !== 'new') {
        await this.checkEtlStatus(data);
      }
    } catch (e) {
      this.isAthenaReady = false;
      this.isAthenaFinished = false;
    }
    if (this.config) {
      this.config.language = this.config.language || localStorage.getItem('language') || 'en';
    }
  }

  public needsInventoryScope() {
    const operationalModeRuleCode = 'operational_mode';
    return needsInventoryScope(
      this.shopifyScopes,
      this.shopifyScopesKnown,
      Boolean(this.isValidateRule(operationalModeRuleCode, 'unlocked')),
    );
  }

  public needsInventoryTransferScope() {
    return needsInventoryTransferScope(this.shopifyScopes, this.shopifyScopesKnown);
  }

  public hasInventoryTransferPermission() {
    return hasInventoryTransferPermission(this.shopifyScopes, this.shopifyScopesKnown);
  }

  public validateRule(rules: any = []) {
    if (this.rules.items.length === 0) {
      return {isValidate: true, rule: ''};
    }

    let result: boolean = true;
    let notValidateRule: string = '';
    for (const ruleToCheck of rules) {
      const validateResult = this.isValidateRule(
        ruleToCheck.code,
        ruleToCheck.value || '',
      );
      result = result && validateResult;
      if (!validateResult) {
        notValidateRule = ruleToCheck.code;
      }
    }

    return {isValidate: result, resource: notValidateRule || ''};
  }

  public isValidateRule(code: string, validData: string = '') {

    const isValidate = this.checkValidateRule(code, validData);
    if (isValidate) {
      return true;
    }
    // map the premium measure code to the paid measures feature
    const fallbackCode = this.mapOldRuleCodeToNew(code);
    if (!fallbackCode) {
      return false;
    }
    const isValidateFallback =  this.checkValidateRule(fallbackCode, validData);
    return isValidateFallback;
  }

  public checkValidateRule(code: string, validData: string = '') {
    let value: string | number = '';
    let isExist = false;
    let rule: RuleModel = new RuleModel();
    for (const item of this.rules.items) {
      if (item.code === code) {
        rule = item;
        isExist = true;
        break;
      }
    }

    value = rule.value;
    if (!isExist) {
      for (const item of this.fullRules.items) {
        if (item.code === code) {
          rule = item;
        }
      }
      value = rule.defaultValue;
    }
    if (value === undefined) {
      return true;
    }

    switch (rule.type) {
      case 'number': {
        if (Number(value) === -1) {
          return true;
        } else {
          return Number(validData) - Number(value) < 0;
        }
        break;
      }
      default: {
        return validData === value;
        break;
      }
    }
    return true;
  }

  public saveFavoriteReport(isSave: boolean = true, data: any = {}) {
    const favorites: any = this.config.favoriteReports || [];
    if (isSave) {
      // add the current dashboard to favorite list
      let isExist: boolean = false;
      for (const item of favorites) {
        if (item.type === data.type && item.id === data.id) {
          isExist = true;
        }
      }
      if (!isExist) {
        favorites.push(data);
      }
    } else {
      // remove this current dashboard from favorite list
      let index: number = 0;
      for (const item of favorites) {
        if (item.type === data.type && item.id === data.id) {
          break;
        }
        index++;
      }
      if (index < favorites.length) {
        favorites.splice(index, 1);
      }
    }
    try {
      this.config.favoriteReports = favorites;
      this.saveSettings();
    } catch (e: any) {
      // retry to save
      this.saveSettings();
    }
    EventBus.$emit('update-favorite-report-change-status');
  }

  public getFavoriteStatus(id: number = 0, type: string = '') {
    const favorites: any = this.config.favoriteReports || [];
    for (const item of favorites) {
      if (item.type === type && item.id === id) {
        return true;
      }
    }
    return false;
  }

   public async loadEtlStatus() {
    let res: any;
    const preload = [];
    preload.push(this.resourceCount('orders'));
    preload.push(this.resourceCount('products'));
    preload.push(this.resourceCount('customers'));
    preload.push(this.resourceCount('locations'));
    preload.push(this.resourceCount('inventory_items'));
    preload.push(this.resourceCount('custom_collections'));
    preload.push(this.resourceCount('smart_collections'));
    preload.push(this.resourceCount('collections'));
    // allSettled, not all: one count failing must not sink the rest. The
    // endpoint answers 400 'Resource is not supported' for a resource the
    // deployed assisty-bot doesn't know yet — `collections` on any release
    // before the GraphQL extract — and a rejection here left
    // EtlStatusPage.loadETLImportedCount stuck on its loading flag with no
    // rows rendered at all.
    await Promise.allSettled(preload);
  }



  public async resourceCount(resource: string) {
    const key = `resourceCount:${resource}`;
    if (this._inflight.has(key)) {
      return this._inflight.get(key);
    }
    const promise = (async () => {
      try {
        let res: any;
        res = await get(`/etl_metrics/count_resource/${resource}`);

        const countData = {
          resource,
          apiCount: res.apiCount,
          modelCount: Math.min(res.modelCount, res.apiCount),
        };

        switch (resource) {
          case 'orders': {
            this.orderCount = countData;
            break;
          }
          case 'products': {
            this.productCount = countData;
            break;
          }
          case 'customers': {
            this.customerCount = countData;
            break;
          }
          case 'locations': {
            this.locationCount = countData;
            break;
          }
          case 'inventory_items': {
            this.inventoryItemCount = countData;
            break;
          }
          case 'custom_collections': {
            this.customCollection = countData;
            break;
          }
          case 'smart_collections': {
            this.smartCollection = countData;
            break;
          }
          case 'collections': {
            this.collectionCount = countData;
            break;
          }
        }
      } finally {
        this._inflight.delete(key);
      }
    })();
    this._inflight.set(key, promise);
    return promise;
  }

  public async forceSyncResource(resource: string) {
    const res: any = await get(`/etl/trigger_incremental?resources=${resource}`);
    return res.data;
  }

  // Product sync methods moved to src/collections/product_sync_jobs.ts
  // Use: import { productSyncJobs } from '@/collections/product_sync_jobs';

  public isTrial() {
    // compare the trialEndAt with current date
    return this.currentPackage.trialEndAt && Date.parse(this.currentPackage.trialEndAt + '') > Date.now();
  }

  public async saveMultipleStoreStockCombinationRule(isCombined: boolean) {
    await put(`/multistores/inventory_combine`, {
      combination: isCombined,
    });
  }

  public async canSubscribePaidPlan() {
    const restrictedPlans = ['developer', 'test'];
    const shopifyPlan = this.information.plan_display_name.toLowerCase();
    const assistyTestStores = ['assisty-demo.myshopify.com', 'dariustest2.myshopify.com', 'the-best-megastore-test.myshopify.com'];
    for (const store of assistyTestStores) {
      if (this.shopName === store) {
        return true;
      }
    }
    for (const plan of restrictedPlans) {
      if (shopifyPlan.includes(plan)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Switch to the store with the given storeId
   * @param storeId
   * @returns {token: string, store_name: string}
   */
  public async switchStore(storeId: string) {
    const res: any = await get(`/switch_store/${storeId}`);
    return res.data;
  }

  /**
   * Get the list of stores in the same group as the current store
   * @returns {id: string, name: string}[]
   */
  public async getGroupStores() {
    const res: any = await get(`/store_groups`);
    return res.data;
  }

  public isPremiumMeasure(measureCode: string) {
    return PREMIUM_MEASURE_CODES.includes(measureCode);
  }

  public mapOldRuleCodeToNew(measureCode: string) {
    // if premium measure code, return true
    if (this.isPremiumMeasure(measureCode)) {
      return FEATURES.PAID_MEASURES;
    }

    return '';
  }

  public async warmOlaps() {
    // Skip for Free package
    if (this.currentPackage.billingPackageId === 14) {
      return;
    }
    const warmupCall = post('sessions/warm-caches', {}).catch((e) => {
      console.error(e);
    });
    const timeout = new Promise((resolve) => setTimeout(resolve, 5000));
    await Promise.race([warmupCall, timeout]);
  }

  private async checkEtlStatus(data: any = {}) {
    if (this.id < 3030) {
      this.isAthenaFinished = true;
      this.isAthenaReady = true;
      this.isProductsReady = true;
      this.isSalesOrderOlapReady = true;
      this.isSalesOrderOlapFinished = true;
      this.isInventoryLevelsReady = true;
    } else {
      let olapCurrentInventoryLevelsReady = false;
      let salesOrderVariantOlapReady = false;
      let productsTransformReady = false;

      let olapCurrentInventoryLevelsFinished = false;
      let salesOrderVariantOlapFinished = false;
      let productsTransformFinished = false;

      for (const etlStatus of this.etlStatuses.items) {
        const startTimeStr = etlStatus.startTime ? etlStatus.startTime.split('T')[0] : '';
        const limitStartTimeStr = etlStatus.limitStartTime ? etlStatus.limitStartTime.split('T')[0] : '';
        const startTime = new Date(startTimeStr).setHours(0, 0, 0, 0);
        const limitStartTime = new Date(limitStartTimeStr).setHours(0, 0, 0, 0);
        let readyStartTime = new Date(this.installedAt).setHours(0, 0, 0, 0);
        readyStartTime = new Date(readyStartTime).setMonth(new Date(readyStartTime).getMonth() - 6);

        if (readyStartTime < limitStartTime) {
          readyStartTime = limitStartTime;
        }

        if (etlStatus.resource === 'sales_order_variant_olap') {
          if (startTime <= readyStartTime) {
            salesOrderVariantOlapReady = true;
          }
          if (startTime <= limitStartTime) {
            salesOrderVariantOlapFinished = true;
          }
        }

        if (etlStatus.resource === 'olap_current_inventory_levels') {
          if (etlStatus.lastUpdatedAt) {
            olapCurrentInventoryLevelsReady = true;
            olapCurrentInventoryLevelsFinished = true;
          }
        }

        // Products readiness: check both etl_data_period and transform_request
        if (etlStatus.resource === 'products') {
          if (etlStatus.etlType === 'transform_request') {
            if (startTime <= limitStartTime) {
              productsTransformReady = true;
              productsTransformFinished = true;
            }
          }
        }

        // Free plan: products transform_request counts as inventory levels ready
        if (this.currentPackage.price === 0) {
          if (etlStatus.resource === 'products' && etlStatus.etlType === 'transform_request') {
            if (startTime <= limitStartTime) {
              olapCurrentInventoryLevelsReady = true;
              olapCurrentInventoryLevelsFinished = true;
            }
          }
        }
      }

      const productsReady = productsTransformReady;
      const productsFinished = productsTransformFinished;

      // Expose individual readiness flags
      this.isProductsReady = productsReady;
      this.isSalesOrderOlapReady = salesOrderVariantOlapReady;
      this.isSalesOrderOlapFinished = salesOrderVariantOlapFinished;
      this.isInventoryLevelsReady = olapCurrentInventoryLevelsReady;

      if (olapCurrentInventoryLevelsReady && salesOrderVariantOlapReady) {
        if (olapCurrentInventoryLevelsFinished && salesOrderVariantOlapFinished) {
          this.isAthenaFinished = true;
        }
        this.isAthenaReady = true;
      } else {
        if (!this.orderCount.resource) {
          await this.resourceCount('orders');
        }
        if (this.orderCount.apiCount === 0) {
          salesOrderVariantOlapReady = true;
          salesOrderVariantOlapFinished = true;
          this.isSalesOrderOlapReady = true;
          this.isSalesOrderOlapFinished = true;
        }

        if (!this.productCount.resource) {
          await this.resourceCount('products');
        }
        if (this.productCount.apiCount === 0) {
          olapCurrentInventoryLevelsReady = true;
          olapCurrentInventoryLevelsFinished = true;
          this.isInventoryLevelsReady = true;
          productsTransformReady = true;
          productsTransformFinished = true;
          this.isProductsReady = true;
        }

        if (olapCurrentInventoryLevelsFinished && salesOrderVariantOlapFinished) {
          this.isAthenaReady = true;
          this.isAthenaFinished = true;
        }
      }
    }
  }
}
